import { Component, Input, OnInit, ChangeDetectionStrategy, inject } from "@angular/core";
import { ModalController, IonButton, IonContent, IonFooter, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonSearchbar, IonTitle, IonToolbar } from "@ionic/angular";
import { PlatformModeContentDirective } from "../../../../../directive/platform-mode-content.directive";
import { NgTemplateOutlet } from "@angular/common";
import { TranslatePipe } from "@ngx-translate/core";
import { NoContentComponent } from "src/components/shared-components/components/general-components/no-content/no-content.component";
import { AreaTree, AreaTreeNode } from "../../../class/general-classes/area-tree";

@Component({
    selector: "app-popup-environment-tree",
    templateUrl: "./popup-environment-tree.component.html",
    styleUrls: ["./popup-environment-tree.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, IonContent, IonFooter, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonSearchbar, IonTitle, IonToolbar, PlatformModeContentDirective, NoContentComponent, NgTemplateOutlet, TranslatePipe]
})
export class PopupEnvironmentTreeComponent implements OnInit {
    private readonly _modalCtrl = inject(ModalController);

    private _levelIcons: string[] = ["assets/img/building.svg", "assets/img/section.svg", "assets/img/floor.svg"];
    /** Map of nodes by their key for quick lookup */
    private _nodesByKey: Map<string, AreaTreeNode> = new Map<string, AreaTreeNode>();

    /** Root nodes of the full (merged) area tree */
    public treeAreas: AreaTreeNode[] = [];
    public displayedAreas: AreaTreeNode[] = [];

    /** Merged area tree, see buildAreaTree() */
    @Input() areas: AreaTreeNode[] = [];
    /** Key of the selected node */
    @Input() selectedValue: string | null = null;

    public ngOnInit(): void {
        this.treeAreas = AreaTree.clone(this.areas);
        this.indexNodes(this.treeAreas);
        this.expandAncestorsOf(this.selectedValue);
        this.displayedAreas = this.treeAreas;
    }

    /**
     * Filter the tree by area name and auto-expand branches that contain a match.
     * Clearing the search restores the full tree, keeping the selected area revealed.
     */
    public onSearch(event: any): void {
        const term: string = (event?.target?.value ?? "").trim().toLowerCase();

        if (term) {
            this.displayedAreas = this.filterTree(this.treeAreas, term);
        } else {
            this.expandAncestorsOf(this.selectedValue);
            this.displayedAreas = this.treeAreas;
        }
    }

    /**
     * Toggle the visibility of a node's children
     */
    public onToggleNode(node: AreaTreeNode): void {
        node.expanded = !node.expanded;
    }

    /**
     * Select an area (single selection)
     */
    public onSelectNode(node: AreaTreeNode): void {
        this.selectedValue = node.key;
    }

    /**
     * Get the icon for a given tree level (falls back to the last one if deeper)
     */
    public getLevelIcon(level: number): string {
        return this._levelIcons[Math.min(level, this._levelIcons.length - 1)];
    }

    /**
     * Exit without choosing an option
     */
    public onClickCancel(): void {
        this._modalCtrl.dismiss();
    }

    /**
     * Confirm the selection and pass the selected node back to the parent component
     */
    public onClickConfirm(): void {
        this._modalCtrl.dismiss({
            selectedValue: this.selectedValue,
            selectedNode: AreaTree.find(this.areas, this.selectedValue),
        });
    }

    /**
     * Index every node of the tree by key.
     */
    private indexNodes(nodes: AreaTreeNode[]): void {
        nodes.forEach((node) => {
            this._nodesByKey.set(node.key, node);
            this.indexNodes(node.children);
        });
    }

    /**
     * Return the branches whose node (or any descendant) name matches the term.
     * Nodes that lead to a match are expanded so the match is revealed.
     */
    private filterTree(nodes: AreaTreeNode[], term: string): AreaTreeNode[] {
        return nodes.reduce<AreaTreeNode[]>((acc, node) => {
            const selfMatches: boolean = node.name?.toLowerCase().includes(term);
            const matchingChildren: AreaTreeNode[] = this.filterTree(node.children, term);

            if (selfMatches || matchingChildren.length) {
                acc.push(new AreaTreeNode({
                    ...node,
                    // keep the whole subtree under a direct match, otherwise only matching branches
                    children: selfMatches ? node.children : matchingChildren,
                    // expand whenever there are matching descendants to reveal
                    expanded: matchingChildren.length > 0,
                }));
            }

            return acc;
        }, []);
    }

    /**
     * Expand every ancestor of the given node so that it is visible on open.
     */
    private expandAncestorsOf(key: string | null): void {
        let parent = this._nodesByKey.get(this._nodesByKey.get(key)?.parentKey);
        while (parent) {
            parent.expanded = true;
            parent = this._nodesByKey.get(parent.parentKey);
        }
    }
}
